import { AfterViewInit, Component, Inject, ViewChild } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { DoppiaMaterialitaService } from 'src/app/services/doppia-materialita/doppia-materialita.service';
import { ESRS } from 'src/app/services/ESRS/esrs.service';
import { Tematica } from 'src/app/services/tematiche/tematiche.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { SpinnerOverlayComponent } from '../../spinner-overlay/spinner-overlay.component';

@Component({
  selector: 'app-dialog-survey-check-esrs',
  standalone: false,
  templateUrl: './dialog-survey-check-esrs.component.html',
  styleUrl: './dialog-survey-check-esrs.component.scss',
})
export class DialogSurveyCheckEsrsComponent implements AfterViewInit {
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;

  firstTemaWithSottoTemiIndex: number = -1;

  public esrs: ESRS[] = [];

  /**
   * Riflette la selezione a livello di Tematica (radice).
   * - true  → nessun elemento figlio è selezionato
   * - false → almeno un elemento figlio è selezionato
   *
   * Il valore viene aggiornato centralmente tramite _updateTematicaState(),
   * richiamato dopo ogni caricamento dati e dopo ogni interazione utente.
   */
  tematicaIncludi: boolean = true;

  constructor(
    public dialogRef: MatDialogRef<DialogSurveyCheckEsrsComponent>,
    public doppiaMaterialitaService: DoppiaMaterialitaService,
    private readonly utilityService: UtilityService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      domandaTematica: string;
      isAziendale: boolean;
      idAmbito: string;
      idDoppiaMaterialita: string;
      idTematica: string;
      readOnly: boolean;
    },
  ) {}

  ngAfterViewInit(): void {
    this._getEsrsById();
  }

  // ---------------------------------------------------------------------------
  // API
  // ---------------------------------------------------------------------------

  private _getEsrsById(): void {
    const { idTematica, idAmbito, idDoppiaMaterialita } = this.data;

    if (!idTematica) return;

    this.spinnerOver.show();
    this.doppiaMaterialitaService
      .getEsrsByIdDP(idDoppiaMaterialita, idAmbito, idTematica)
      .pipe(finalize(() => this.spinnerOver.hide()))
      .subscribe({
        next: (esito) => {
          this.esrs = esito;
          this.firstTemaWithSottoTemiIndex = this.esrs.findIndex((t) => t.sottoTemi?.length > 0);
          this._updateTematicaState();
        },
        error: (err) => console.error(err),
      });
  }

  private _putEsrs(): void {
    const { idTematica, idAmbito, idDoppiaMaterialita } = this.data;

    if (!idTematica || !idDoppiaMaterialita || !idAmbito || !this.esrs) return;

    this.spinnerOver.show();
    this.doppiaMaterialitaService
      .putEsrsTematicaDP(idDoppiaMaterialita, idAmbito, idTematica, this.esrs)
      .pipe(finalize(() => this.spinnerOver.hide()))
      .subscribe({
        next: (tematica: Tematica) => this.dialogRef.close(tematica),
        error: (err) => {
          console.error(err);
          this.utilityService.openDialog({
            descrizione:
              err.error?.message ??
              'Si è verificato un errore imprevisto. I dati non sono stati salvati.',
            bottoni: [{ nome_btn: 'Ok' }],
          });
        },
      });
  }

  // ---------------------------------------------------------------------------
  // Azioni utente
  // ---------------------------------------------------------------------------

  conferma(): void {
    // La selezione è valida se la Tematica è attiva a livello radice
    // oppure se almeno un figlio (a qualunque livello) è selezionato.
    const isValid = this.tematicaIncludi || this.checkIfAnyLevelSelected(this.esrs);

    if (!isValid) {
      this.utilityService.openSnackBar('Selezionare almeno un elemento', '', {
        duration: 2000,
        panelClass: ['red-snackbar'],
      });
      return;
    }

    this._putEsrs();
  }

  /**
   * Gestisce il click sulla checkbox della Tematica (livello radice).
   *
   * - check   → deseleziona tutti i figli; la tematica diventa l'unico elemento attivo
   * - uncheck → operazione non significativa: la tematica diventa automaticamente
   *             false non appena un figlio viene selezionato; l'handler la ignora
   *             e ripristina lo stato coerente tramite _updateTematicaState().
   */
  onChangeTematica(event: MatCheckboxChange): void {
    if (event.checked) {
      this._deselectAll(this.esrs);
      this.tematicaIncludi = true;
    } else {
      this.tematicaIncludi = false;
    }
  }

  onChangeTema(event: MatCheckboxChange, tema: ESRS): void {
    if (!tema) return;

    tema.includi = event.checked;

    if (event.checked) {
      // Deseleziona tutti i figli del tema selezionato
      this._deselectAll(tema.sottoTemi);
    }

    this._updateTematicaState();
  }

  onChangeSottoTema(
    event: MatCheckboxChange,
    tema: ESRS | undefined,
    sottoTema: ESRS | undefined,
  ): void {
    if (!sottoTema) return;

    sottoTema.includi = event.checked;

    if (event.checked) {
      this._deselectAll(sottoTema.sottoTemi);
      this._clearTemaIfChildSelected(tema);
    }

    this._updateTematicaState();
  }

  onChangeSottoSottoTema(
    event: MatCheckboxChange,
    tema: ESRS | undefined,
    sottoTema: ESRS | undefined,
    sottoSottoTema: ESRS | undefined,
  ): void {
    if (!sottoTema || !sottoSottoTema) return;

    sottoSottoTema.includi = event.checked;

    if (event.checked) {
      sottoTema.includi = false;
      this._clearTemaIfChildSelected(tema);
    }

    this._updateTematicaState();
  }

  // ---------------------------------------------------------------------------
  // Stato derivato
  // ---------------------------------------------------------------------------

  /**
   * Controlla ricorsivamente se almeno un elemento nella lista
   * (a qualunque livello di profondità) ha includi = true.
   */
  checkIfAnyLevelSelected(esrsList?: ESRS[]): boolean {
    return (
      esrsList?.some((esrs) => esrs.includi || this.checkIfAnyLevelSelected(esrs.sottoTemi)) ??
      false
    );
  }

  // ---------------------------------------------------------------------------
  // Metodi privati di utilità
  // ---------------------------------------------------------------------------

  /**
   * Aggiorna lo stato della checkbox Tematica in base alla selezione dei figli.
   * Da invocare dopo ogni mutazione dello stato ESRS.
   *
   * Invariante: tematicaIncludi === !checkIfAnyLevelSelected(esrs)
   */
  private _updateTematicaState(): void {
    if (this.checkIfAnyLevelSelected(this.esrs)) {
      this.tematicaIncludi = false;
    }
  }

  /**
   * Deseleziona ricorsivamente tutti gli elementi della lista fornita
   * e i loro discendenti.
   */
  private _deselectAll(esrsList: ESRS[] | undefined): void {
    esrsList?.forEach((esrs) => {
      esrs.includi = false;
      if (esrs.sottoTemi?.length) {
        this._deselectAll(esrs.sottoTemi);
      }
    });
  }

  /**
   * Se almeno un figlio del tema è selezionato, il tema stesso
   * non può essere contemporaneamente selezionato.
   */
  private _clearTemaIfChildSelected(tema: ESRS | undefined): void {
    if (!tema) return;

    const hasSelectedChild = tema.sottoTemi?.some(
      (sottoTema) => sottoTema.includi || sottoTema.sottoTemi?.some((sst) => sst.includi),
    );

    if (hasSelectedChild) {
      tema.includi = false;
    }
  }

  /**
   * Restituisce `true` se l'elemento NON è selezionato direttamente
   * (includi = false) ma almeno un suo discendente è attivo.
   *
   * Usato per mostrare lo stato "indeterminate" sui checkbox padre,
   * rendendo immediatamente visibile che una selezione parziale
   * esiste tra i figli.
   *
   * @param item - Nodo ESRS da valutare (Tema o SottoTema)
   */
  isIndeterminate(item: ESRS): boolean {
    if (item.includi) return false;
    return this.checkIfAnyLevelSelected(item.sottoTemi);
  }
}
